import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import { useNotification } from "../../providers/NotificationProvider";
import {
  fetchAvailableWorkers,
  fetchReleasedWorkers,
  fetchMemberWithWorkers,
  registerMemberAchievementsToMarket,
} from "../../api/memberApi";

export default function WorkerListPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { memberId, member } = useAuth();
  const notify = useNotification();

  const [tab, setTab] = useState(0); // 0=アクティブ, 1=解任済み
  const [workers, setWorkers] = useState([]);
  const [releasedWorkers, setReleasedWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [searchText, setSearchText] = useState("");

  // 市場メンバー判定: industryId が "9999" 以外 = 市場メンバー
  const isMarketMember = member?.industryId !== "9999";

  const loadWorkers = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const [active, released] = await Promise.all([
        fetchAvailableWorkers(memberId).catch(() => []),
        fetchReleasedWorkers(memberId).catch(() => []),
      ]);
      setWorkers(Array.isArray(active) ? active : []);
      setReleasedWorkers(Array.isArray(released) ? released : []);
    } catch (err) {
      notify.apiError(err);
    } finally {
      setLoading(false);
    }
  }, [memberId, notify]);

  useEffect(() => {
    loadWorkers();
  }, [loadWorkers]);

  const handleRegisterToMarket = async () => {
    setRegistering(true);
    try {
      const memberData = await fetchMemberWithWorkers(memberId);
      await registerMemberAchievementsToMarket(memberData);
      notify.success(intl.formatMessage({ id: "worker.marketRegister.success" }));
    } catch (err) {
      notify.apiError(err);
    } finally {
      setRegistering(false);
    }
  };

  const currentList = tab === 0 ? workers : releasedWorkers;

  const filteredList = searchText
    ? currentList.filter((w) => {
        const q = searchText.toLowerCase();
        return (
          w.workerName?.toLowerCase().includes(q) ||
          w.projectName?.toLowerCase().includes(q) ||
          w.programName?.toLowerCase().includes(q)
        );
      })
    : currentList;

  const renderTable = (list, isReleased) => {
    if (list.length === 0) {
      return (
        <Typography color="text.secondary">
          {intl.formatMessage({ id: isReleased ? "worker.noReleasedWorkers" : "worker.noWorkers" })}
        </Typography>
      );
    }
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 600 }}>
                {intl.formatMessage({ id: "worker.name" })}
              </TableCell>
              {!isReleased && (
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "worker.project" })} /{" "}
                  {intl.formatMessage({ id: "worker.program" })}
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 600 }}>
                {intl.formatMessage({ id: "worker.department" })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((worker) => (
              <TableRow
                key={worker.workerId}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(`/workers/${worker.workerId}`, isReleased ? { state: { released: true } } : undefined)}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {worker.workerName}
                    </Typography>
                    {isReleased && (
                      <Chip
                        label={intl.formatMessage({ id: "worker.released" })}
                        size="small"
                        color="default"
                      />
                    )}
                  </Box>
                </TableCell>
                {!isReleased && (
                  <TableCell>
                    {worker.projectName ? (
                      <Chip label={worker.projectName} size="small" color="primary" variant="outlined" />
                    ) : worker.programName ? (
                      <Chip label={worker.programName} size="small" color="secondary" variant="outlined" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {worker.departmentName || "—"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {intl.formatMessage({ id: "menu.workers" })}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder={intl.formatMessage({ id: "common.search" })}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {isMarketMember && tab === 0 && (
            <Button
              variant="outlined"
              startIcon={<StorefrontIcon />}
              onClick={handleRegisterToMarket}
              disabled={registering}
            >
              {registering
                ? intl.formatMessage({ id: "worker.marketRegister.loading" })
                : intl.formatMessage({ id: "worker.marketRegister.button" })}
            </Button>
          )}
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={intl.formatMessage({ id: "worker.tab.active" })} />
        <Tab
          label={intl.formatMessage({ id: "worker.tab.released" })}
          icon={<HistoryIcon fontSize="small" />}
          iconPosition="start"
        />
      </Tabs>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        renderTable(filteredList, tab === 1)
      )}
    </Box>
  );
}
